import { useHead } from '@vueuse/head'
import { useRoute } from 'vue-router'
import { computed } from 'vue'
import {
  SITE_NAME,
  PUBLIC_URL,
  DEFAULT_OG_IMAGE,
  DEFAULT_DESCRIPTION,
  TITLE_SUFFIX
} from '@/constants/seo'

/**
 * Centralized per-route SEO helper.
 *
 * Usage (Options API):
 *   import { useSeo } from '@/composables/useSeo'
 *   export default {
 *     setup() {
 *       useSeo({
 *         title: 'About Us',
 *         description: '…',
 *         type: 'website'
 *       })
 *     },
 *     // existing options...
 *   }
 *
 * Each field has a sensible default pulled from constants/seo.js. Pass a
 * function/ref to make it reactive.
 *
 * Routes whose `meta.robots === 'noindex,nofollow'` automatically emit a
 * noindex tag, regardless of the title/description configured. This makes
 * App.vue's global useHead the right place to wire the robots meta;
 * per-view useSeo() then layers brand-tuned title/description over it.
 *
 * @param {object} options
 * @param {string} [options.title]       Page title (will get site suffix appended)
 * @param {string} [options.description] Meta description (≤ 160 chars recommended)
 * @param {string} [options.image]       Absolute or root-relative OG/Twitter image URL
 * @param {string} [options.type]        og:type (default 'website')
 * @param {string} [options.canonical]   Canonical path (default: current route path)
 */
export function useSeo(options = {}) {
  const route = useRoute()

  const headPayload = computed(() => {
    const title = options.title ? `${options.title}${TITLE_SUFFIX}` : SITE_NAME
    const description = options.description || DEFAULT_DESCRIPTION
    const image = options.image
      ? (options.image.startsWith('http') ? options.image : `${PUBLIC_URL}${options.image}`)
      : DEFAULT_OG_IMAGE
    const type = options.type || 'website'
    const canonicalPath = options.canonical || route?.path || '/'
    const canonical = `${PUBLIC_URL}${canonicalPath === '/' ? '/' : canonicalPath.replace(/\/$/, '')}`

    return {
      title,
      meta: [
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:type', content: type },
        { property: 'og:url', content: canonical },
        { property: 'og:image', content: image },
        { property: 'og:site_name', content: SITE_NAME },
        { property: 'og:locale', content: 'en_US' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: image }
      ],
      link: [
        { rel: 'canonical', href: canonical }
      ]
    }
  })

  useHead(headPayload)
}
